/**
 * Base API Client
 *
 * Provides a thin wrapper around fetch for API calls.
 */

import { getMailAssertion, isMailRoute, MAIL_ASSERTION_HEADER } from './mailAuth';

const BASE_URL = '/api/v1';

export interface ApiResponse<T> {
    success: boolean;
    data: T;
    error?: {
        code: string;
        message: string;
    };
    meta?: {
        requestId: string;
        timestamp: string;
    };
}

/**
 * The Error `fetchApi` throws once a response exists: the HTTP status rides along so a caller
 * can tell "denied" (403 — an answer, act on it) from "broken" (500) or "bad input" (400)
 * without parsing the message, which is written for the user and may change.
 */
export interface ApiError extends Error {
    status?: number;
    /** `error.code` from the JSON body, when the server sent one (e.g. 'PERIOD_FILED'). */
    code?: string;
}

function apiError(message: string, status: number, code?: string): ApiError {
    const err = new Error(message) as ApiError;
    err.status = status;
    if (code) err.code = code;
    return err;
}

async function mailHeaders(opts: { interactive: boolean; forceRefresh?: boolean }): Promise<Record<string, string>> {
    const assertion = await getMailAssertion(opts);
    return assertion ? { [MAIL_ASSERTION_HEADER]: assertion } : {};
}

/**
 * Fetch wrapper with JSON handling and error normalization.
 *
 * A request that can send mail as the signed-in person (`mail-routes.json`) carries the
 * person's Microsoft mail token (SEC-005). A direct send answers 428 MAIL_SIGN_IN_REQUIRED
 * when that token was missing or expired — nothing was sent then, so it is asked for again
 * (popup if needed) and the request repeated once.
 */
export async function fetchApi<T>(
    url: string,
    options?: RequestInit
): Promise<T> {
    const mailRoute = isMailRoute(options?.method, url);
    const send = async (extra: Record<string, string>) => fetch(`${BASE_URL}${url}`, {
        ...options,
        headers: {
            'Content-Type': 'application/json',
            ...options?.headers,
            ...extra,
        },
    });

    let response = await send(mailRoute ? await mailHeaders({ interactive: true }) : {});

    if (mailRoute && response.status === 428) {
        const code = await response.clone().json()
            .then((b: ApiResponse<unknown>) => b.error?.code).catch(() => undefined);
        if (code === 'MAIL_SIGN_IN_REQUIRED') {
            const retry = await mailHeaders({ interactive: true, forceRefresh: true });
            if (Object.keys(retry).length > 0) response = await send(retry);
        }
    }

    if (response.status === 204) {
        return undefined as T;
    }

    // Session expired — redirect to login
    if (response.status === 401) {
        const { redirectToLogin } = await import('../utils/auth-redirect');
        redirectToLogin();
        throw apiError('Session expired', response.status);
    }

    // Gracefully handle non-JSON responses (e.g. SWA gateway timeout HTML page,
    // Azure 5xx error page). Without this the caller sees a confusing
    // "Unexpected token < is not valid JSON" SyntaxError.
    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
        const text = await response.text().catch(() => '');
        const snippet = text.replace(/<[^>]+>/g, ' ').trim().slice(0, 200);
        if (response.status >= 500) {
            throw apiError(`Server error ${response.status}. This often means the request took too long or the upstream timed out. Try again with a shorter input.${snippet ? ` Details: ${snippet}` : ''}`, response.status);
        }
        throw apiError(`Unexpected non-JSON response (status ${response.status}).${snippet ? ` ${snippet}` : ''}`, response.status);
    }

    let body: ApiResponse<T>;
    try {
        body = await response.json() as ApiResponse<T>;
    } catch {
        throw apiError(`Server returned an invalid response (status ${response.status}).`, response.status);
    }

    if (!response.ok || !body.success) {
        throw apiError(body.error?.message || `API error: ${response.status}`, response.status,
            body.error?.code);
    }

    return body.data;
}
